import React from 'react';
import { cn } from '../../utils/cn';
import type { ProductRecommendationsSectionProps, ProductCardItem } from './types';
import type { GridConfig } from '../top-categories/types';

/**
 * Формирует классы для grid-сетки на основе конфигурации
 */
function buildGridClasses(gridConfig?: GridConfig): string {
  if (!gridConfig) {
    // Дефолтная конфигурация: 2 колонки на мобильных, 5 на больших экранах
    return 'grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5';
  }

  const classes: string[] = [];
  
  // Маппинг для grid-cols классов Tailwind
  const gridColsMap: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
    8: 'grid-cols-8',
    9: 'grid-cols-9',
    10: 'grid-cols-10',
    11: 'grid-cols-11',
    12: 'grid-cols-12',
  };
  
  // Для мобильных устройств используем flex с прокруткой, если указано
  if (gridConfig.mobile) {
    // На мобильных: горизонтальный свайп/скролл с подсказкой следующей карточки
    classes.push('flex overflow-x-auto scrollbar-hide pb-4 gap-3 snap-x snap-mandatory');
    classes.push('md:grid md:gap-3');
  } else {
    // Если mobile не указан, используем grid везде
    classes.push('grid gap-3');
  }

  // Добавляем классы для разных breakpoints
  if (gridConfig.sm && gridColsMap[gridConfig.sm]) {
    classes.push(`sm:${gridColsMap[gridConfig.sm]}`);
  }
  if (gridConfig.md && gridColsMap[gridConfig.md]) {
    classes.push(`md:${gridColsMap[gridConfig.md]}`);
  }
  if (gridConfig.lg && gridColsMap[gridConfig.lg]) {
    classes.push(`lg:${gridColsMap[gridConfig.lg]}`);
  }
  if (gridConfig.xl && gridColsMap[gridConfig.xl]) {
    classes.push(`xl:${gridColsMap[gridConfig.xl]}`);
  }

  return classes.join(' ');
}

/**
 * Универсальный компонент для отображения подборок товаров (рекомендаций)
 * Поддерживает различные типы реко-блоков через систему провайдеров
 */
export async function ProductRecommendationsSection({
  provider,
  translations = {},
  gridConfig,
  mobileSlidesPerView,
  langCode,
  className = '',
  gridClassName,
  renderItem,
  providerParams,
  providerContext,
  minItems = 1,
  limit,
}: ProductRecommendationsSectionProps) {
  // Получаем параметры для провайдера
  const defaultParams = providerParams ?? provider.getDefaultParams(providerContext);
  
  // Если параметры null, это означает, что провайдер не может получить данные (например, нет cookies)
  if (defaultParams === null) {
    return null;
  }
  
  // Если указан лимит, добавляем его в параметры
  const finalParams = limit ? { ...defaultParams, limit } : defaultParams;

  // Получаем данные через провайдер
  let items: ProductCardItem[] = [];
  try {
    items = await provider.fetch(finalParams);
  } catch (error) {
    console.error('[ProductRecommendationsSection] Error fetching recommendations:', error);
    return null;
  }

  // Проверяем минимальное количество элементов
  if (!Array.isArray(items) || items.length < minItems) {
    return null;
  }

  // Применяем лимит, если указан
  const limitedItems = limit && limit > 0 ? items.slice(0, limit) : items;

  // Заголовок из переводов
  const title = translations.title;

  // Формируем классы для grid
  const finalGridClassName = gridClassName || buildGridClasses(gridConfig);
  const isMobileCarousel = Boolean(gridConfig?.mobile);
  const mobileSlides = typeof mobileSlidesPerView === 'number' && mobileSlidesPerView > 0
    ? mobileSlidesPerView
    : undefined;
  const mobileItemStyle: React.CSSProperties | undefined = mobileSlides
    ? { flex: `0 0 ${100 / mobileSlides}%` }
    : undefined;

  return (
    <section className={cn('mt-12', className)}>
      {title && (
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        </div>
      )}
      <div className={cn('mt-4', finalGridClassName)}>
        {limitedItems.map((item, index) => {
          if (!renderItem) {
            // Если renderItem не указан, возвращаем null (должен быть передан)
            return null;
          }

          const key = item?.code || item?.id || `recommendation-${index}`;
          const element = renderItem(item, index);

          // В мобильном режиме (горизонтальный свайп) оборачиваем элементы,
          // чтобы контролировать ширину карточек (например, 2.2 на экране).
          if (isMobileCarousel) {
            return (
              <div key={key} className="snap-start shrink-0" style={mobileItemStyle}>
                {element}
              </div>
            );
          }

          if (React.isValidElement(element)) {
            return React.cloneElement(element, {
              key: element.key || key,
            });
          }

          return <React.Fragment key={key}>{element}</React.Fragment>;
        })}
      </div>
    </section>
  );
}

